import {
  CategoryKey,
  FIN_CATEGORY_BY_KEY,
  type FinCategory,
  isContribution,
} from '#dictionary'
import { formatGrowthRate, formatKeyValue } from '#formatting'

import { FinancialRecord } from '../financialRecords'
import projectValue from '../projectValue'
import createTimelineRecord from '../timeline/createTimelineRecord'
import type { TimelineRecord } from '../timeline/TimelineRecord'
import type { FinancialRecordSummary } from '../types'
import TimelineGenerator from './TimelineGenerator'
import type { GenerationResult } from './types'

type Operation = {
  operation: string
  amount: string
  runningValue: number
}

type CurrentYearCache = {
  distributionValue: number
  assetTransferValue: number
  companyContributionValue: number
  employeeContributionValue: number
  drawDownValue: number
  timeline: TimelineRecord[]
}

const defaultCurrentYearCache: CurrentYearCache = {
  distributionValue: 0,
  assetTransferValue: 0,
  companyContributionValue: 0,
  employeeContributionValue: 0,
  drawDownValue: 0,
  timeline: [],
}

// TODO(ZL-961): `currentYearCache` is per-year mutable state. Safe today only
// because `_generate` calls `resetCache()` at line 91 before any read. Adding
// a new mutable field that persists across calls would reintroduce the
// ZL-957-class bug. Convert to pure functions to remove this footgun.
class AssetGenerator extends TimelineGenerator {
  private readonly contributions: readonly FinancialRecord[]
  private readonly assetTransfers: readonly FinancialRecord[]
  private currentYearCache: CurrentYearCache = defaultCurrentYearCache
  private readonly incomeForRequiredDistributionCategory: FinCategory

  constructor(
    record: FinancialRecord,
    children: readonly FinancialRecord[],
    assetTransfers: readonly FinancialRecord[],
    private readonly predictionsStartDate: Date,
    private readonly predictionsEndYear: number
  ) {
    super(record)
    this.assetTransfers = assetTransfers

    this.contributions = children.filter(it => isContribution(it.finCategory))

    const incomeForRequiredDistributionCategory = FIN_CATEGORY_BY_KEY.get(
      CategoryKey.catIncomeRequiredDistributionsTaxable
    )

    if (!incomeForRequiredDistributionCategory) {
      throw new Error(
        'AssetGenerator requires record categories with key catIncomeRequiredDistributionsTaxable'
      )
    }
    this.incomeForRequiredDistributionCategory =
      incomeForRequiredDistributionCategory

    this.resetCache()
  }

  _generate(
    year: number,
    drawDownRecipients?: FinancialRecordSummary[]
  ): GenerationResult {
    this.resetCache()

    if (this.shouldGenerateAssetOpeningTimeline(year)) {
      this.generateAssetOpeningTimeline()
    }

    this.generateContributionsTimeline(year)
    this.generateRequiredDistributionsTimeline(year)
    this.generateAssetTransfersTimeline(year)
    this.generateDrawDownsTimeline(year, drawDownRecipients)
    const newAssetValue = this.generateAssetsGrowthTimeline(year)

    const assetIncomeValue = Math.max(
      0,
      newAssetValue - this.previousYearFinalValue
    )
    this.generateAssetsIncomeTimeline(year, assetIncomeValue)

    if (this.shouldGenerateAssetsClosingTimeline(year)) {
      this.generateAssetsClosingTimeline(year)
    }

    return {
      timeline: this.currentYearCache.timeline,
      finalValue: newAssetValue,
    }
  }

  private resetCache(): void {
    this.currentYearCache = {
      distributionValue: 0,
      assetTransferValue: 0,
      companyContributionValue: 0,
      employeeContributionValue: 0,
      drawDownValue: 0,
      timeline: [],
    }
  }

  private generateDrawDownsTimeline(
    year: number,
    drawDownRecipients?: FinancialRecordSummary[]
  ): void {
    if (!drawDownRecipients) {
      return
    }

    const currentAssetAsARecipient = drawDownRecipients.find(
      it => it.id === this.record.id
    )

    if (!currentAssetAsARecipient) {
      return
    }

    const drawDownCategoryKey = this.record.finCategory.a_withdrawalCategoryKey
    if (!drawDownCategoryKey) {
      return
    }

    const drawdownValue = currentAssetAsARecipient.value

    this.currentYearCache.drawDownValue = drawdownValue

    this.currentYearCache.timeline.push(
      createTimelineRecord({
        year,
        value: drawdownValue,
        description: this.buildDrawDownDescription(year, drawdownValue),
        categoryKey: drawDownCategoryKey,
        financialRecord: this.record,
        intent: 'DrawDown',
      })
    )
  }

  private generateContributionsTimeline(year: number): void {
    this.contributions.forEach(contribution => {
      const isContributionMatchYear =
        contribution.startAt.getFullYear() <= year &&
        contribution.endAt.getFullYear() >= year

      if (!isContributionMatchYear) {
        return
      }

      const contributionValue = contribution.projectedValue(year)

      if (
        contribution.finCategory.key === CategoryKey.catContributionsEmployer
      ) {
        this.currentYearCache.companyContributionValue += contributionValue
        return
      }

      this.currentYearCache.employeeContributionValue += contributionValue

      this.currentYearCache.timeline.push(
        createTimelineRecord({
          year,
          value: contributionValue,
          description: this.buildContributionDescription(
            year,
            contribution.finCategory.name,
            contributionValue
          ),
          categoryKey: contribution.finCategory.key,
          financialRecord: contribution,
        })
      )
    })
  }

  private generateRequiredDistributionsTimeline(year: number): void {
    if (!this.record.finCategory.a_isRequiredDistributionsBase) {
      return
    }

    const distribution = this.record.owner.getRequiredDistribution(year)

    if (!distribution) {
      return
    }

    const { distributionPeriod, distributionPercentage } = distribution
    const distributionValue = this.previousYearFinalValue / distributionPeriod
    this.currentYearCache.distributionValue += distributionValue

    this.currentYearCache.timeline.push(
      createTimelineRecord({
        year,
        value: distributionValue,
        description: this.buildRequiredDistributionDescription(
          year,
          distributionPercentage,
          distributionPeriod,
          distributionValue
        ),
        financialRecord: this.record,
        categoryKey: this.incomeForRequiredDistributionCategory.key,
      })
    )
  }

  // TODO: #should validate drawdown and open categories and apply only if both are present
  // #should display warning otherwise
  private generateAssetTransfersTimeline(year: number): void {
    this.assetTransfers.forEach(assetTransfer => {
      const isAssetTransferMatchYear =
        assetTransfer.startAt.getFullYear() <= year &&
        assetTransfer.endAt.getFullYear() >= year

      if (!isAssetTransferMatchYear) {
        return
      }

      const rawTransferValue = assetTransfer.projectedValue(year)
      const isTransferOut = assetTransfer.t_fromAssetId === this.record.id
      const assetTransferValue = isTransferOut
        ? Math.min(rawTransferValue, Math.max(0, this.previousYearFinalValue))
        : rawTransferValue
      const transferValue = isTransferOut
        ? -assetTransferValue
        : assetTransferValue
      const categoryKey = isTransferOut
        ? this.record.finCategory.a_withdrawalCategoryKey
        : this.record.finCategory.a_topUpCategoryKey

      if (!categoryKey) {
        return
      }

      this.currentYearCache.assetTransferValue += transferValue

      this.currentYearCache.timeline.push(
        createTimelineRecord({
          year,
          value: assetTransferValue,
          description: this.buildAssetTransferDescription(
            year,
            assetTransfer.finCategory.name,
            assetTransferValue
          ),
          financialRecord: assetTransfer,
          categoryKey,
        })
      )
    })
  }

  private buildAssetTransferDescription(
    year: number,
    categoryName: string,
    assetTransferValue: number
  ): string {
    return formatKeyValue(`Asset Transfer (${year})`, [
      ['Type', categoryName],
      ['Amount', `$${assetTransferValue.toFixed(0)}`],
      ['Year', year.toString()],
    ])
  }

  private generateAssetsGrowthTimeline(year: number): number {
    const [startYear, endYear] = this.record.timelineYearsRange
    if (endYear === year) {
      return this.previousYearFinalValue
    }

    const valueWithGrowth = projectValue(
      this.previousYearFinalValue,
      this.record.getGrowthRateForYear(year),
      Math.max(year - 1, startYear),
      year
    )

    const finalValue = Math.max(
      0,
      valueWithGrowth -
        this.currentYearCache.distributionValue +
        this.currentYearCache.employeeContributionValue +
        this.currentYearCache.companyContributionValue +
        this.currentYearCache.assetTransferValue -
        this.currentYearCache.drawDownValue
    )

    const assetGrowthTimeline = createTimelineRecord({
      year,
      value: finalValue,
      description: this.assetGrowthDescription(year, valueWithGrowth),
      financialRecord: this.record,
    })

    this.currentYearCache.timeline.push(assetGrowthTimeline)

    return finalValue
  }

  private generateAssetsIncomeTimeline(
    year: number,
    assetIncomeValue: number
  ): void {
    const recurrentInflowCategoryKey =
      this.record.finCategory.a_recurrentInflowCategoryKey

    if (!recurrentInflowCategoryKey) {
      return
    }

    const newAssetValue = this.previousYearFinalValue + assetIncomeValue

    this.currentYearCache.timeline.push(
      createTimelineRecord({
        year,
        value: assetIncomeValue,
        description: this.buildAssetIncomeDescription(
          year,
          newAssetValue,
          assetIncomeValue
        ),
        categoryKey: recurrentInflowCategoryKey,
        financialRecord: this.record,
      })
    )

    this.currentYearCache.timeline.push(
      createTimelineRecord({
        year,
        value: assetIncomeValue,
        description: this.buildAutoReinvestmentDescription(
          year,
          newAssetValue,
          assetIncomeValue
        ),
        categoryKey: CategoryKey.catContributionsAuto,
        financialRecord: this.record,
      })
    )
  }

  private shouldGenerateAssetOpeningTimeline(year: number): boolean {
    const { startAt } = this.record
    return year === startAt.getFullYear() && startAt > this.predictionsStartDate
  }

  private generateAssetOpeningTimeline(): void {
    const topUpCategoryKey = this.record.finCategory.a_topUpCategoryKey

    if (!topUpCategoryKey) {
      // TODO: ZL-537 — report to Sentry, show user-friendly message that record was skipped
      // eslint-disable-next-line no-console
      console.error(
        'Asset opening category not found for record',
        this.record.id
      )
      return
    }

    this.currentYearCache.timeline.push(
      createTimelineRecord({
        year: this.record.startAt.getFullYear(),
        value: this.record.annualValue,
        description: this.assetOpeningDescription(),
        categoryKey: topUpCategoryKey,
        financialRecord: this.record,
      })
    )
  }

  private shouldGenerateAssetsClosingTimeline(year: number): boolean {
    if (!this.record.finCategory.a_isLiquid) {
      return false
    }

    const [, endYear] = this.record.timelineYearsRange
    const timelineEndYear = this.predictionsEndYear

    if (endYear >= timelineEndYear) {
      return false
    }

    return year === endYear
  }

  private generateAssetsClosingTimeline(
    _year: number,
    assetFinalValue: number = this.previousYearFinalValue
  ): void {
    const [, endYear] = this.record.timelineYearsRange

    const closeCategoryKey = this.record.finCategory.a_closeCategoryKey

    if (!closeCategoryKey) {
      return
    }

    // TODO: #must instead create one timeline record with double entry
    // to simplify the calculations
    this.currentYearCache.timeline.push(
      createTimelineRecord({
        year: endYear,
        value: 0,
        description: this.assetClosureDescription(endYear),
        categoryKey: closeCategoryKey,
        financialRecord: this.record,
      }),
      createTimelineRecord({
        year: endYear,
        value: assetFinalValue,
        description: this.assetConversionDescription(endYear, assetFinalValue),
        categoryKey: closeCategoryKey,
        financialRecord: this.record,
      })
    )
  }

  private assetOpeningDescription(): string {
    return formatKeyValue(
      `Buying ${this.record.name} (${this.record.startAt.getFullYear()})`,
      [
        ['Type', this.record.finCategory.name],
        ['Amount', `$${this.record.annualValue.toFixed(0)}`],
        ['Year', this.record.startAt.getFullYear().toString()],
      ]
    )
  }

  private assetClosureDescription(assetEndYear: number): string {
    return formatKeyValue(`Asset Closure (${assetEndYear})`, [
      ['Type', 'Asset Liquidation'],
      ['Amount', `$0`],
      ['Year', `End of ${assetEndYear}`],
    ])
  }

  private assetConversionDescription(
    assetEndYear: number,
    assetFinalValue: number
  ): string {
    return formatKeyValue(`Asset Conversion (${assetEndYear})`, [
      ['Type', 'Conversion to Cash'],
      ['Amount', `+$${assetFinalValue.toFixed(0)}`],
      ['Source', this.record.finCategory.name],
    ])
  }

  private buildContributionDescription(
    year: number,
    categoryName: string,
    contributionValue: number
  ): string {
    return formatKeyValue(`Contribution (${year})`, [
      ['Type', categoryName],
      ['Amount', `$${contributionValue.toFixed(0)}`],
      ['Year', year.toString()],
    ])
  }

  private buildRequiredDistributionDescription(
    year: number,
    distributionPercentage: number,
    distributionPeriod: number,
    distributionValue: number
  ): string {
    return formatKeyValue(`Required Distribution (${year})`, [
      ['Percentage', `${distributionPercentage.toFixed(2)}%`],
      ['Period', `1/${distributionPeriod}`],
      ['Base Value', `$${this.previousYearFinalValue.toFixed(0)}`],
      ['Distribution', `$${distributionValue.toFixed(0)}`],
    ])
  }

  private assetGrowthDescription(
    year: number,
    valueWithGrowth: number
  ): string {
    const operations: Operation[] = []

    let runningValue = this.previousYearFinalValue

    operations.push({
      operation: 'Starting Balance',
      amount: '',
      runningValue: runningValue,
    })

    runningValue = valueWithGrowth
    const growthAmount = valueWithGrowth - this.previousYearFinalValue

    operations.push({
      operation: `Growth (${formatGrowthRate(this.record.growthRate)})`,
      amount: `+$${growthAmount.toFixed(0)}`,
      runningValue: runningValue,
    })

    // Distributions (if any)
    if (this.currentYearCache.distributionValue > 0) {
      runningValue -= this.currentYearCache.distributionValue

      operations.push({
        operation: 'Distribution',
        amount: `-$${this.currentYearCache.distributionValue.toFixed(0)}`,
        runningValue: runningValue,
      })
    }

    // Employee contributions (if any)
    if (this.currentYearCache.employeeContributionValue > 0) {
      runningValue += this.currentYearCache.employeeContributionValue
      operations.push({
        operation: 'Employee Contribution',
        amount: `+$${this.currentYearCache.employeeContributionValue.toFixed(0)}`,
        runningValue: runningValue,
      })
    }

    if (this.currentYearCache.companyContributionValue > 0) {
      runningValue += this.currentYearCache.companyContributionValue
      operations.push({
        operation: 'Company Match',
        amount: `+$${this.currentYearCache.companyContributionValue.toFixed(0)}`,
        runningValue: runningValue,
      })
    }

    if (this.currentYearCache.assetTransferValue !== 0) {
      runningValue += this.currentYearCache.assetTransferValue

      const rothConversionValue = this.currentYearCache.assetTransferValue
      const sign = rothConversionValue > 0 ? '+' : '-'

      operations.push({
        operation: 'Roth Conversion',
        amount: `${sign}$${Math.abs(rothConversionValue).toFixed(0)}`,
        runningValue: runningValue,
      })
    }

    if (this.currentYearCache.drawDownValue !== 0) {
      runningValue -= this.currentYearCache.drawDownValue
      operations.push({
        operation: 'Draw Down',
        amount: `-$${this.currentYearCache.drawDownValue.toFixed(0)}`,
        runningValue: runningValue,
      })
    }

    const tableRows = operations
      .map(
        op =>
          `| ${op.operation} | ${op.amount} | $${op.runningValue.toFixed(0)} |`
      )
      .join('\n')

    const tableHeader =
      '| Operation | Amount | Value After |\n|-----------|--------|-------------|'
    const table = `${tableHeader}\n${tableRows}`

    return `## Asset: ${this.record.name} (${year})

${table}`
  }

  private buildAssetIncomeDescription(
    year: number,
    newAssetValue: number,
    assetIncomeValue: number
  ): string {
    const growthRate = (
      (assetIncomeValue / this.previousYearFinalValue) *
      100
    ).toFixed(2)

    return formatKeyValue(`Asset Income (${year})`, [
      ['Asset Value', `$${newAssetValue.toFixed(0)}`],
      ['Previous Value', `$${this.previousYearFinalValue.toFixed(0)}`],
      ['Income Generated', `$${assetIncomeValue.toFixed(0)}`],
      ['Growth Rate', `${growthRate}%`],
    ])
  }

  private buildAutoReinvestmentDescription(
    year: number,
    newAssetValue: number,
    assetIncomeValue: number
  ): string {
    const growthRate = (
      (assetIncomeValue / this.previousYearFinalValue) *
      100
    ).toFixed(2)

    return formatKeyValue(`Auto Re-investment (${year})`, [
      ['Asset Value', `$${newAssetValue.toFixed(0)}`],
      ['Previous Value', `$${this.previousYearFinalValue.toFixed(0)}`],
      ['Income Generated', `$${assetIncomeValue.toFixed(0)}`],
      ['Re-invested Amount', `$${assetIncomeValue.toFixed(0)}`],
      ['Growth Rate', `${growthRate}%`],
    ])
  }

  private buildDrawDownDescription(
    year: number,
    drawdownValue: number
  ): string {
    return formatKeyValue(`Draw Down (${year})`, [
      ['Type', 'Asset Draw Down'],
      ['Amount', `$${drawdownValue.toFixed(0)}`],
      ['Year', year.toString()],
      ['Asset', this.record.name],
    ])
  }
}

export default AssetGenerator
